import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { VaultKeTheme } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const styles: { [key: string]: React.CSSProperties } = {
    page: {
      minHeight: '100vh',
      backgroundColor: VaultKeTheme.colors.background,
      color: VaultKeTheme.colors.text,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: VaultKeTheme.spacing.xl,
    },
    container: {
      maxWidth: '500px',
      width: '100%',
    },
    logoContainer: {
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    logo: {
      width: '80px',
      height: '80px',
      backgroundColor: VaultKeTheme.colors.primary,
      borderRadius: '50%',
      margin: '0 auto',
      marginBottom: VaultKeTheme.spacing.lg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '32px',
      fontWeight: 'bold',
      color: VaultKeTheme.colors.background,
    },
    logoText: {
      fontSize: VaultKeTheme.typography.h1.fontSize,
      fontWeight: VaultKeTheme.typography.h1.fontWeight,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    card: {
      backgroundColor: VaultKeTheme.colors.surface,
      borderRadius: VaultKeTheme.borderRadius.xl,
      padding: VaultKeTheme.spacing.xxl,
      boxShadow: VaultKeTheme.shadows.lg,
    },
    title: {
      ...VaultKeTheme.typography.h2,
      color: VaultKeTheme.colors.text,
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.sm,
    },
    subtitle: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.textSecondary,
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xl,
    },
    formGroup: {
      marginBottom: VaultKeTheme.spacing.lg,
    },
    label: {
      display: 'block',
      fontSize: VaultKeTheme.typography.body2.fontSize,
      fontWeight: '600',
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.sm,
    },
    input: {
      width: '100%',
      padding: VaultKeTheme.spacing.md,
      border: `1px solid ${VaultKeTheme.colors.border}`,
      borderRadius: VaultKeTheme.borderRadius.md,
      backgroundColor: VaultKeTheme.colors.surfaceVariant,
      color: VaultKeTheme.colors.text,
      fontSize: VaultKeTheme.typography.body1.fontSize,
      outline: 'none',
      transition: 'border-color 0.2s ease',
    },
    inputFocus: {
      borderColor: VaultKeTheme.colors.primary,
    },
    inputError: {
      borderColor: VaultKeTheme.colors.error,
    },
    passwordContainer: {
      position: 'relative',
    },
    passwordToggle: {
      position: 'absolute',
      right: VaultKeTheme.spacing.md,
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      color: VaultKeTheme.colors.textSecondary,
      cursor: 'pointer',
      fontSize: '18px',
    },
    errorText: {
      fontSize: VaultKeTheme.typography.caption.fontSize,
      color: VaultKeTheme.colors.error,
      marginTop: VaultKeTheme.spacing.xs,
    },
    forgotPassword: {
      textAlign: 'right',
      marginBottom: VaultKeTheme.spacing.lg,
    },
    forgotPasswordLink: {
      color: VaultKeTheme.colors.primary,
      textDecoration: 'none',
      fontSize: VaultKeTheme.typography.body2.fontSize,
      fontWeight: '600',
    },
    button: {
      width: '100%',
      padding: VaultKeTheme.spacing.lg,
      backgroundColor: VaultKeTheme.colors.primary,
      color: VaultKeTheme.colors.background,
      border: 'none',
      borderRadius: VaultKeTheme.borderRadius.md,
      fontSize: VaultKeTheme.typography.button.fontSize,
      fontWeight: VaultKeTheme.typography.button.fontWeight,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      marginBottom: VaultKeTheme.spacing.lg,
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    divider: {
      display: 'flex',
      alignItems: 'center',
      margin: `${VaultKeTheme.spacing.xl} 0`,
    },
    dividerLine: {
      flex: 1,
      height: '1px',
      backgroundColor: VaultKeTheme.colors.border,
    },
    dividerText: {
      padding: `0 ${VaultKeTheme.spacing.md}`,
      color: VaultKeTheme.colors.textSecondary,
      fontSize: VaultKeTheme.typography.body2.fontSize,
    },
    signUpContainer: {
      textAlign: 'center',
    },
    signUpText: {
      color: VaultKeTheme.colors.textSecondary,
      fontSize: VaultKeTheme.typography.body2.fontSize,
    },
    signUpLink: {
      color: VaultKeTheme.colors.primary,
      textDecoration: 'none',
      fontWeight: '600',
    },
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Basic validation
    if (!identifier.trim()) {
      setErrors(prev => ({ ...prev, identifier: 'Email or phone number is required' }));
      return;
    }
    if (!password) {
      setErrors(prev => ({ ...prev, password: 'Password is required' }));
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier: identifier.trim(),
          password: password,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Store auth token
        localStorage.setItem('authToken', data.data.token);

        // Store user data
        localStorage.setItem('userData', JSON.stringify(data.data.user));

        // Redirect to user dashboard (admins can access user dashboard as well)
        navigate('/dashboard');
      } else {
        setErrors({ general: data.error || 'Login failed' });
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ general: 'Network error. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ ...styles.page, paddingBottom: '80px' }}>
      <div style={styles.container}>
        <div style={styles.logoContainer}>
          <div style={styles.logo}>VK</div>
          <h1 style={styles.logoText}>VaultKe</h1>
        </div>

        <div style={styles.card}>
          <h2 style={styles.title}>Welcome Back</h2>
          <p style={styles.subtitle}>Sign in to your account</p>

          <form onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email or Phone</label>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Enter your email or phone"
                style={{
                  ...styles.input,
                  ...(errors.identifier ? styles.inputError : {}),
                }}
              />
              {errors.identifier && <div style={styles.errorText}>{errors.identifier}</div>}
            </div>

            {errors.general && (
              <div style={{
                ...styles.errorText,
                textAlign: 'center',
                marginBottom: VaultKeTheme.spacing.lg,
                padding: VaultKeTheme.spacing.md,
                backgroundColor: VaultKeTheme.colors.error + '20',
                borderRadius: VaultKeTheme.borderRadius.md,
                border: `1px solid ${VaultKeTheme.colors.error}`,
              }}>
                {errors.general}
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Password</label>
              <div style={styles.passwordContainer}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  style={{
                    ...styles.input,
                    ...(errors.password ? styles.inputError : {}),
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.passwordToggle}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {errors.password && <div style={styles.errorText}>{errors.password}</div>}
            </div>

            <div style={styles.forgotPassword}>
              <a href="/forgot-password" style={styles.forgotPasswordLink}>Forgot Password?</a>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              style={{
                ...styles.button,
                ...(isLoading ? styles.buttonDisabled : {}),
              }}
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          <div style={styles.divider}>
            <div style={styles.dividerLine}></div>
            <span style={styles.dividerText}>OR</span>
            <div style={styles.dividerLine}></div>
          </div>

          <div style={styles.signUpContainer}>
            <span style={styles.signUpText}>Don't have an account? </span>
            <a href="/register" style={styles.signUpLink}>Sign Up</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;